import capitalize from "lodash/capitalize";
import { Fragment, type FunctionComponent } from "react";

import { BaseLinkContextProvider } from "~/components/common/base-link/context";
import { MenuItemAccordionList } from "~/components/hamburger-menu/hamburger-menu-default/hamburger-menu-item/accordion-list";
import { MenuItemHighlight } from "~/components/hamburger-menu/hamburger-menu-default/hamburger-menu-item/highlight";
import { MenuItemTopic } from "~/components/hamburger-menu/hamburger-menu-default/hamburger-menu-item/topics";
import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import { sendGA4Tracking } from "~/components/tracking/google-analytics-4/apis";
import { tracking } from "~/data";
import type {
  BurgerMenuTabsItems,
  BurgerMenuTopTab,
} from "~/lib/app/hooks/scmp-pwa-burger-menu-config";
import { useScmpPwaBurgerMenuConfig } from "~/lib/app/hooks/scmp-pwa-burger-menu-config";
import { useEditionValue } from "~/lib/edition";
import { useCurrentPageType } from "~/lib/router/hooks";

import { useBurgerMenuTopics } from "./hooks";
import { Container } from "./styles";

type Props = {
  className?: string;
};

export const HamburgerMenuList: FunctionComponent<Props> = ({ className }) => {
  const { scmpPwaBurgerMenuConfigState } = useScmpPwaBurgerMenuConfig();
  const currentEdition = useEditionValue();
  const tobTab = scmpPwaBurgerMenuConfigState.result?.topTab as BurgerMenuTopTab;
  const tabs = scmpPwaBurgerMenuConfigState.result?.tabs as BurgerMenuTabsItems[];
  const topics = useBurgerMenuTopics();
  const pageType = useCurrentPageType();

  const sendTracking = (text: string, headerName: string) => {
    const name = capitalize(text).replaceAll(/scmp/gi, "SCMP");
    const nameGa4Tracking = text.replaceAll(/scmp/gi, "SCMP");
    const headerNameLowerCase = headerName?.toLowerCase();

    const computeLabel = () => {
      if (headerNameLowerCase === "highlights") {
        return `Menu/Highlights/${name}` as const;
      } else if (headerName) {
        return `Menu/Click/${headerName}/${name}` as const;
      } else {
        return "";
      }
    };

    const computeGA4Parameters = () => {
      if (headerNameLowerCase === "highlights") {
        return { highlights: nameGa4Tracking };
      } else if (headerName.toLowerCase() === "topics") {
        return { topic: nameGa4Tracking };
      } else if (nameGa4Tracking.toLowerCase() === "latest") {
        return { destination: `${headerName.toLowerCase()}_latest` };
      } else {
        return { destination: nameGa4Tracking };
      }
    };

    const label = computeLabel();

    if (label) {
      sendGATracking({
        action: "Menu/Click",
        category: "Menu",
        label,
      });
    }
    if (headerName) {
      sendGA4Tracking({
        action: "click",
        category: "menu",
        customized_parameters: {
          action_type: "redirect",
          edition: currentEdition,
          menu_type: "hamburger",
          ...computeGA4Parameters(),
        },
        subcategory: "section",
      });
    }
  };

  const renderMenuItem = (items: BurgerMenuTopTab & BurgerMenuTabsItems) => {
    if (items) {
      switch (items.type) {
        case "highlight":
          return (
            <MenuItemHighlight
              items={items.items}
              onClick={(name, headerName) => sendTracking(name, headerName)}
            />
          );
        case "accordion-list":
          return (
            <MenuItemAccordionList
              items={items.items}
              label={items?.button?.text}
              type={items.type}
              onClick={(name, headerName) => sendTracking(name, headerName)}
            />
          );
        case "topics":
          return (
            <MenuItemTopic
              label={items?.button?.text}
              reference={topics}
              type={items.type}
              onClick={(name, headerName) => sendTracking(name, headerName)}
            />
          );
      }
    }
  };
  return (
    <Container className={className}>
      <BaseLinkContextProvider
        customQueryParameters={{
          module: tracking.module.HamburgerMenu,
          pgtype: pageType,
        }}
      >
        {renderMenuItem(tobTab)}
        {tabs?.map((tab, index) => <Fragment key={index}>{renderMenuItem(tab)}</Fragment>)}
      </BaseLinkContextProvider>
    </Container>
  );
};
